import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Divider,
  Button
} from '@mui/material';
import { 
  People as PeopleIcon, 
  Stars as StarsIcon, 
  Image as ImageIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  AccountBalance as BalanceIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

function Dashboard() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [stats, setStats] = useState({
    totalPrompts: 0,
    totalUsers: 0,
    totalBonuses: 0,
    meanPurchaseValue: 0,
    totalPurchaseValue: 0,
    totalAttachments: 0,
    bonusRequestsCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { token } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Fetch users and their bonuses
        const usersResponse = await axios.get('/users/get-all-users', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const users = usersResponse.data;
        
        // Fetch prompts
        const promptsResponse = await axios.get('/prompts', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const prompts = promptsResponse.data;

        // Get all user bonuses to calculate purchase values
        const allBonuses = [];
        for (const user of users) {
          try {
            const bonusesResponse = await axios.get(`/users/bonuses/${encodeURIComponent(user.user_email)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            allBonuses.push(...(bonusesResponse.data.bonuses || []));
          } catch (err) {
            console.error(`Failed to fetch bonuses for user ${user.user_email}:`, err);
          }
        }

        const totalBonuses = allBonuses.length;

        const [attachmentsResponse] = await Promise.all([
          axios.get('/attachments/count', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        // Fetch bonus usage requests count
        let bonusRequestsCount = 0;
        try {
          const bonusRequestsResponse = await axios.get('/users/bonus_usage_requests', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          bonusRequestsCount = bonusRequestsResponse.data.length;
        } catch (err) {
          console.error('Failed to fetch bonus usage requests count:', err);
        }

        setStats({
          totalPrompts: prompts.length,
          totalUsers: users.length,
          totalBonuses,
          totalAttachments: attachmentsResponse.data.total_count,
          bonusRequestsCount
        });
        setError('');
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError(t('failed_to_load_dashboard'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token, t]);

  if (!token) {
    return (
      <Box p={3}>
        <Alert severity="error">{t('no_token_available')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, color, gradient, subtitle }) => (
    <Card
      sx={{
        height: '100%',
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          zIndex: 0
        }
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1, p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              width: 40,
              height: 40,
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const handleQuickAction = (action) => {
    switch (action) {
      case 'users':
        navigate('/client/clients');
        break;
      case 'bonuses':
        navigate('/client/bonuses');
        break;
      case 'balance':
        navigate('/client/my-balance');
        break;
      case 'settings':
        navigate('/client/client-settings');
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <DashboardIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('dashboard_overview')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('dashboard_subtitle')}
        </Typography>
      </Box>

      {/* Main Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('bonus_usage_requests')}
            value={stats.bonusRequestsCount}
            icon={<StarsIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)"
            subtitle={t('pending_approvals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_users')}
            value={stats.totalUsers}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            subtitle={t('active_user_accounts')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_bonuses')}
            value={stats.totalBonuses}
            icon={<StarsIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            subtitle={t('issued_bonuses')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_attachments')}
            value={stats.totalAttachments}
            icon={<ImageIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            subtitle={t('generated_assets')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_prompts')}
            value={stats.totalPrompts}
            icon={<ChatIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            subtitle={t('available_prompts')}
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ p: 3, background: 'background.paper', border: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
          {t('quick_actions')}
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => handleQuickAction('users')}
            sx={{ 
              borderRadius: 2,
              fontWeight: 'bold',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'white',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('view_users')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<StarsIcon />}
            onClick={() => handleQuickAction('bonuses')}
            sx={{ 
              borderRadius: 2,
              fontWeight: 'bold',
              borderColor: 'secondary.main',
              color: 'secondary.main',
              '&:hover': {
                bgcolor: 'secondary.main',
                color: 'white',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('manage_bonuses')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BalanceIcon />}
            onClick={() => handleQuickAction('balance')}
            sx={{ 
              borderRadius: 2,
              fontWeight: 'bold',
              borderColor: 'success.main',
              color: 'success.main',
              '&:hover': {
                bgcolor: 'success.main',
                color: 'white',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('check_balance')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => handleQuickAction('settings')}
            sx={{ 
              borderRadius: 2,
              fontWeight: 'bold',
              borderColor: 'info.main',
              color: 'info.main',
              '&:hover': {
                bgcolor: 'info.main',
                color: 'white',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('settings')}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}

export default Dashboard; 